/**
 * React Hook for Stockout Predictions
 * ====================================
 * Fetches and manages stockout prediction data
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';

export interface StockoutPrediction {
  upc: string;
  product_name: string;
  predicted_oos_pct: number;
  risk_level: 'critical' | 'warning' | 'healthy';
  revenue_at_risk: number;
  current_state: {
    oos_pct: number | null;
    store_wos: number | null;
    dc_wos: number | null;
    velocity: number | null;
    store_eoh: number | null;
    dc_eoh: number | null;
  };
}

export interface StockoutPredictionsResponse {
  fiscal_week: number;
  target_fiscal_week: number;
  threshold_used: number;
  threshold_pct: number;
  horizon_weeks: number;
  predictions: StockoutPrediction[];
  summary: {
    total_at_risk: number;
    critical_count: number;
    warning_count: number;
    total_revenue_at_risk: number;
  };
  model: {
    mape: number | null;
    version: string;
  };
}

export function useStockoutPredictions(
  fiscalWeek?: number,
  threshold: number = 0.05,
  horizon: number = 2,
  limit: number = 100,
  sort: 'risk' | 'revenue' = 'revenue',
  retailerId?: string
) {
  return useQuery({
    queryKey: ['stockout-predictions', fiscalWeek, threshold, horizon, limit, sort, retailerId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (fiscalWeek) params.append('fiscal_week', fiscalWeek.toString());
      params.append('threshold', threshold.toString());
      params.append('horizon', horizon.toString());
      params.append('limit', limit.toString());
      params.append('sort', sort);
      if (retailerId) params.append('retailer_id', retailerId);
      
      const queryString = params.toString();
      const response = await apiClient.get<{ data: StockoutPredictionsResponse }>(
        `/ml/predict-stockouts${queryString ? `?${queryString}` : ''}`
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export interface BacktestResults {
  overall: {
    total_predictions: number;
    weeks_tested: number;
    week_range: {
      min: number;
      max: number;
    };
    mape: number;
    mae: number;
    accuracy_pct: number;
    precision: number;
    recall: number;
    f1_score: number;
    confusion_matrix: {
      true_positives: number;
      false_positives: number;
      true_negatives: number;
      false_negatives: number;
    };
    total_revenue_at_risk: number;
    meets_target: boolean;
    target_mape: number;
    assessment: string;
  };
  weekly: Array<{
    fiscal_week: number;
    predictions: number;
    mape: number;
    accuracy: number;
    true_positives: number;
    false_positives: number;
    false_negatives: number;
    revenue_at_risk: number;
  }>;
  best_skus: Array<{
    upc: string;
    product_name: string;
    predictions: number;
    mape: number;
    accuracy: number;
  }>;
  worst_skus: Array<{
    upc: string;
    product_name: string;
    predictions: number;
    mape: number;
    accuracy: number;
  }>;
}

export function useBacktestResults() {
  return useQuery({
    queryKey: ['backtest-results'],
    queryFn: async () => {
      const response = await apiClient.get<{ data: BacktestResults }>('/ml/backtest-results');
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

